import { useState, useEffect } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { TrendingUp, Truck, DollarSign, Users, Star, Loader2, Calendar } from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchAdminStats, fetchWasteDistribution, fetchRevenueTrend } from '../services/adminService'
import type { AdminStats } from '../types'

const WASTE_TYPE_LABELS: Record<string, string> = {
  GENERAL: 'General Waste',
  RECYCLABLE: 'Recyclable',
  ORGANIC: 'Organic',
  EWASTE: 'E-Waste',
  BULK: 'Bulk Items',
}

const WASTE_TYPE_COLORS: Record<string, string> = {
  GENERAL: '#6B7280',
  RECYCLABLE: '#2D7D46',
  ORGANIC: '#FF6B35',
  EWASTE: '#8B5CF6',
  BULK: '#3B82F6',
}

interface WasteItem { type: string; percentage: number; color: string }
interface TrendItem { date: string; revenue: number; pickups: number; subscriptions: number; donations: number }

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
]

export default function Analytics() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [wasteBreakdown, setWasteBreakdown] = useState<WasteItem[]>([])
  const [trend, setTrend] = useState<TrendItem[]>([])
  const [trendDays, setTrendDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [trendLoading, setTrendLoading] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const [data, wasteData] = await Promise.all([
          fetchAdminStats(),
          fetchWasteDistribution(),
        ])
        setStats(data)
        const dist = wasteData?.distribution ?? []
        setWasteBreakdown(
          dist.map((d: { wasteType: string; percentage: number }) => ({
            type: WASTE_TYPE_LABELS[d.wasteType] ?? d.wasteType,
            percentage: d.percentage,
            color: WASTE_TYPE_COLORS[d.wasteType] ?? '#6B7280',
          }))
        )
      } catch {
        toast.error('Failed to load analytics data')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // Load revenue trend separately (changes with date range)
  useEffect(() => {
    async function loadTrend() {
      setTrendLoading(true)
      try {
        const data = await fetchRevenueTrend(trendDays)
        setTrend(data?.trend ?? [])
      } catch {
        toast.error('Failed to load revenue trend')
      } finally {
        setTrendLoading(false)
      }
    }
    loadTrend()
  }, [trendDays])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  const s = stats

  const pickupsByStatus = [
    { status: 'Completed', count: s?.completedPickups ?? 0 },
    { status: 'Pending', count: s?.pendingPickups ?? 0 },
    { status: 'Missed', count: s?.missedPickups ?? 0 },
  ]

  const revenueFormatted = s?.revenueThisMonth != null ? `GHS ${(s.revenueThisMonth / 1000).toFixed(0)}k` : 'GHS 0'
  const revenueGrowth = s?.revenueGrowth != null ? `${s.revenueGrowth > 0 ? '+' : ''}${s.revenueGrowth.toFixed(1)}%` : ''

  // Format trend dates for display
  const formattedTrend = trend.map((t) => ({
    ...t,
    label: new Date(t.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
  }))

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <p className="text-sm text-[#6B7280]">Comprehensive performance metrics and trends</p>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total Pickups', value: s?.totalPickups?.toLocaleString() ?? '0', change: `${s?.completedPickups ?? 0} completed`, up: true, icon: Truck, color: 'bg-primary-600' },
          { label: 'Total Revenue', value: revenueFormatted, change: revenueGrowth, up: (s?.revenueGrowth ?? 0) >= 0, icon: DollarSign, color: 'bg-accent' },
          { label: 'Total Users', value: s?.totalUsers?.toLocaleString() ?? '0', change: `${s?.newUsersThisWeek ?? 0} new this week`, up: true, icon: Users, color: 'bg-blue-500' },
          { label: 'Avg. Rating', value: s?.averageRating?.toFixed(2) ?? '—', change: '/ 5', up: true, icon: Star, color: 'bg-purple-500' },
        ].map(({ label, value, change, up, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              {change && (
                <span className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${up ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  <TrendingUp className="w-3 h-3" />
                  {change}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-[#1A1A2E]">{value}</p>
            <p className="text-xs text-[#6B7280] mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-[#1A1A2E]">Revenue Trend</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">Daily revenue breakdown (GHS)</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-gray-400" />
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTrendDays(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                  trendDays === opt.value
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {trendLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={formattedTrend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                interval={Math.max(0, Math.floor(formattedTrend.length / 8) - 1)}
              />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB', fontSize: '12px' }}
                formatter={(value: number) => [`GHS ${value.toFixed(2)}`, undefined]}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
              <Line type="monotone" dataKey="revenue" name="Total" stroke="#2D7D46" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="pickups" name="Pickups" stroke="#3B82F6" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="subscriptions" name="Subscriptions" stroke="#8B5CF6" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Pickups by Status Chart */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-[#1A1A2E]">Pickups by Status</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">Current breakdown</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={pickupsByStatus} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis dataKey="status" tick={{ fontSize: 12, fill: '#6B7280' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB', fontSize: '12px' }} />
            <Bar dataKey="count" fill="#2D7D46" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Reports + Revenue Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Revenue Summary */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="mb-4">
            <h3 className="font-semibold text-[#1A1A2E]">Revenue Summary</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">Monthly comparison (GHS)</p>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
              <div>
                <p className="text-xs text-green-600 font-medium">This Month</p>
                <p className="text-2xl font-bold text-green-700">GHS {s?.revenueThisMonth?.toLocaleString() ?? 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="text-xs text-[#6B7280] font-medium">Last Month</p>
                <p className="text-2xl font-bold text-[#1A1A2E]">GHS {s?.revenueLastMonth?.toLocaleString() ?? 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-gray-300" />
            </div>
            {s?.revenueGrowth != null && (
              <div className={`text-center p-3 rounded-xl ${s.revenueGrowth >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className={`text-sm font-bold ${s.revenueGrowth >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {s.revenueGrowth >= 0 ? '+' : ''}{s.revenueGrowth.toFixed(1)}% growth
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Reports Summary */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="mb-4">
            <h3 className="font-semibold text-[#1A1A2E]">Reports Summary</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">Current report status</p>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Open', value: s?.openReports ?? 0, color: 'bg-red-500', total: (s?.openReports ?? 0) + (s?.inReviewReports ?? 0) + (s?.resolvedReports ?? 0) },
              { label: 'In Review', value: s?.inReviewReports ?? 0, color: 'bg-yellow-500', total: (s?.openReports ?? 0) + (s?.inReviewReports ?? 0) + (s?.resolvedReports ?? 0) },
              { label: 'Resolved', value: s?.resolvedReports ?? 0, color: 'bg-green-500', total: (s?.openReports ?? 0) + (s?.inReviewReports ?? 0) + (s?.resolvedReports ?? 0) },
            ].map(({ label, value, color, total }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#1A1A2E]">{label}</span>
                  <span className="text-sm font-bold text-[#1A1A2E]">{value}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full">
                  <div
                    className={`h-2 rounded-full ${color} transition-all`}
                    style={{ width: total > 0 ? `${(value / total) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Waste Type Breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-[#1A1A2E]">Waste Type Breakdown</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">Distribution of pickups by waste category</p>
        </div>
        <div className="divide-y divide-gray-50">
          {wasteBreakdown.map((item) => (
            <div key={item.type} className="px-5 py-3.5 flex items-center gap-4">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-sm font-medium text-[#1A1A2E] w-36 flex-shrink-0">{item.type}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{ width: `${item.percentage}%`, backgroundColor: item.color }}
                />
              </div>
              <span className="text-xs font-semibold text-[#1A1A2E] w-10 text-right">{item.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
